import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

const ThemeContext = createContext();

export const themes = {
  light: {
    name: 'Slate',
    primary: '#F1F5F9',
    secondary: '#CBD5E0',
    background: '#0F172A',
    surface: '#1E293B',
    text: '#F1F5F9',
    textSecondary: '#94A3B8',
    border: '#334155',
    accent: '#60A5FA',
    accentLight: '#1E3A8A',
    hover: '#273548',
  },
  dark: {
    name: 'Midnight',
    primary: '#F7FAFC',
    secondary: '#E2E8F0',
    background: '#09090B',
    surface: '#18181B',
    text: '#FAFAFA',
    textSecondary: '#A1A1AA',
    border: '#27272A',
    accent: '#A78BFA',
    accentLight: '#2E1065',
    hover: '#27272A',
  },
  warm: {
    name: 'Warm',
    primary: '#FEF3C7',
    secondary: '#FDE68A',
    background: '#1C1412',
    surface: '#2A1F1A',
    text: '#FEF3C7',
    textSecondary: '#D4A574',
    border: '#3D2E22',
    accent: '#F59E0B',
    accentLight: '#451A03',
    hover: '#352A22',
  },
  cool: {
    name: 'Cool',
    primary: '#E0F2FE',
    secondary: '#BAE6FD',
    background: '#0A1628',
    surface: '#0F2440',
    text: '#E0F2FE',
    textSecondary: '#7DD3FC',
    border: '#164E72',
    accent: '#38BDF8',
    accentLight: '#0C4A6E',
    hover: '#143352',
  },
  forest: {
    name: 'Forest',
    primary: '#DCFCE7',
    secondary: '#BBF7D0',
    background: '#0A1A10',
    surface: '#132A1A',
    text: '#DCFCE7',
    textSecondary: '#6EE7A0',
    border: '#1A4028',
    accent: '#4ADE80',
    accentLight: '#14532D',
    hover: '#1A3520',
  },
  sepia: {
    name: 'Sepia',
    primary: '#FEFCE8',
    secondary: '#FEF9C3',
    background: '#1A1609',
    surface: '#2A2510',
    text: '#FEFCE8',
    textSecondary: '#C8A96E',
    border: '#3D3518',
    accent: '#D97706',
    accentLight: '#451A03',
    hover: '#352E16',
  },
};

export const lightThemes = {
  light: {
    name: 'Slate',
    primary: '#0F172A',
    secondary: '#1E293B',
    background: '#F1F5F9',
    surface: '#FFFFFF',
    text: '#0F172A',
    textSecondary: '#64748B',
    border: '#CBD5E1',
    accent: '#3B82F6',
    accentLight: '#DBEAFE',
    hover: '#E2E8F0',
  },
  dark: {
    name: 'Midnight',
    primary: '#09090B',
    secondary: '#18181B',
    background: '#FAFAFA',
    surface: '#FFFFFF',
    text: '#18181B',
    textSecondary: '#71717A',
    border: '#D4D4D8',
    accent: '#7C3AED',
    accentLight: '#EDE9FE',
    hover: '#F4F4F5',
  },
  warm: {
    name: 'Warm',
    primary: '#451A03',
    secondary: '#78350F',
    background: '#FFFBEB',
    surface: '#FFFFFF',
    text: '#451A03',
    textSecondary: '#92400E',
    border: '#FDE68A',
    accent: '#D97706',
    accentLight: '#FEF3C7',
    hover: '#FEF3C7',
  },
  cool: {
    name: 'Cool',
    primary: '#0C4A6E',
    secondary: '#075985',
    background: '#F0F9FF',
    surface: '#FFFFFF',
    text: '#0C4A6E',
    textSecondary: '#0369A1',
    border: '#BAE6FD',
    accent: '#0EA5E9',
    accentLight: '#E0F2FE',
    hover: '#E0F2FE',
  },
  forest: {
    name: 'Forest',
    primary: '#14532D',
    secondary: '#166534',
    background: '#F0FDF4',
    surface: '#FFFFFF',
    text: '#14532D',
    textSecondary: '#15803D',
    border: '#BBF7D0',
    accent: '#16A34A',
    accentLight: '#DCFCE7',
    hover: '#DCFCE7',
  },
  sepia: {
    name: 'Sepia',
    primary: '#713F12',
    secondary: '#854D0E',
    background: '#FEFCE8',
    surface: '#FFFFFF',
    text: '#713F12',
    textSecondary: '#A16207',
    border: '#FEF08A',
    accent: '#CA8A04',
    accentLight: '#FEF9C3',
    hover: '#FEF9C3',
  },
};

const fontSizes = {
  small: '14px',
  normal: '16px',
  large: '18px',
  'extra-large': '20px',
};

const fontFamilies = {
  'Sans Serif': '"Inter", sans-serif',
  'Serif': '"Lora", "Georgia", serif',
  'Roboto': '"Roboto", "Helvetica Neue", Arial, sans-serif',
  'Merriweather': '"Merriweather", "Georgia", serif',
};

function hexToHsl(hex) {
  let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '0 0% 0%';
  let r = parseInt(result[1], 16);
  let g = parseInt(result[2], 16);
  let b = parseInt(result[3], 16);
  r /= 255; g /= 255; b /= 255;
  let max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  if (max === min) {
    h = s = 0;
  } else {
    let d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: h = 0;
    }
    h /= 6;
  }
  h = Math.round(h * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100);
  return `${h} ${s}% ${l}%`;
}

export function ThemeProvider({ children }) {
  const [currentTheme, setCurrentTheme] = useState('light');
  const [currentFontSize, setCurrentFontSize] = useState('normal');
  const [currentFontFamily, setCurrentFontFamily] = useState('Roboto');
  const [colorMode, setColorMode] = useState('dark');

  const currentThemeRef = useRef('light');
  const colorModeRef = useRef('dark');

  useEffect(() => {
    const savedTheme = localStorage.getItem('reveala-theme') || 'light';
    const savedFontSize = localStorage.getItem('reveala-font-size') || 'normal';
    const savedFontFamily = localStorage.getItem('reveala-font-family') || 'Roboto';
    const savedColorMode = localStorage.getItem('eili-color-mode') || 'dark';

    currentThemeRef.current = savedTheme;
    colorModeRef.current = savedColorMode;

    applyTheme(savedTheme, savedColorMode);
    applyFontSize(savedFontSize);
    const resolvedFont = applyFontFamily(savedFontFamily);

    setCurrentTheme(savedTheme);
    setCurrentFontSize(savedFontSize);
    setCurrentFontFamily(resolvedFont);
    setColorMode(savedColorMode);
  }, []);

  const applyTheme = (themeName, mode) => {
    const themeSet = mode === 'light' ? lightThemes : themes;
    const theme = themeSet[themeName] || themes[themeName] || themes.light;
    const root = document.documentElement;

    Object.entries(theme).forEach(([key, value]) => {
      if (key !== 'name') {
        root.style.setProperty(`--theme-${key}`, value);
      }
    });

    const shadcnMapping = {
      '--background': theme.background,
      '--foreground': theme.text,
      '--card': theme.surface,
      '--card-foreground': theme.text,
      '--popover': theme.surface,
      '--popover-foreground': theme.text,
      '--primary': theme.accent,
      '--primary-foreground': '#FFFFFF',
      '--secondary': theme.hover,
      '--secondary-foreground': theme.text,
      '--muted': theme.hover,
      '--muted-foreground': theme.textSecondary,
      '--accent': theme.accentLight,
      '--accent-foreground': theme.text,
      '--destructive': '#EF4444',
      '--destructive-foreground': '#FFFFFF',
      '--border': theme.border,
      '--input': theme.border,
      '--ring': theme.accent,
    };

    Object.entries(shadcnMapping).forEach(([key, value]) => {
      root.style.setProperty(key, hexToHsl(value));
    });

    if (mode === 'light') {
      root.style.setProperty('--glass-bg', 'rgba(255,255,255,0.65)');
      root.style.setProperty('--glass-border', 'rgba(0,0,0,0.08)');
      root.style.setProperty('--glass-bg-active', 'rgba(0,0,0,0.05)');
      root.style.setProperty('--glass-bg-hover', 'rgba(0,0,0,0.03)');
    } else {
      root.style.setProperty('--glass-bg', 'rgba(255,255,255,0.04)');
      root.style.setProperty('--glass-border', 'rgba(255,255,255,0.08)');
      root.style.setProperty('--glass-bg-active', 'rgba(255,255,255,0.1)');
      root.style.setProperty('--glass-bg-hover', 'rgba(255,255,255,0.06)');
    }

    document.body.style.backgroundColor = theme.background;
    document.body.style.color = theme.text;
    // Sync html background — fills the safe area behind status bar on iOS/Despia
    document.documentElement.style.backgroundColor = theme.background;
    // Update meta theme-color for web/PWA status bar
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) metaThemeColor.setAttribute('content', theme.background);

    // Despia native: set iOS status bar color via bridge command
    if (navigator.userAgent.toLowerCase().includes('despia')) {
      const hex = theme.background.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const cmd = `statusbarcolor://{${r}, ${g}, ${b}}`;
      // Use the despia-native SDK for proper command queuing
      import('despia-native').then(({ default: despia }) => {
        despia(cmd);
        // Android only: set status bar icon color to match theme
        if (navigator.userAgent.toLowerCase().includes('android')) {
          despia(mode === 'light' ? 'statusbartextcolor://black' : 'statusbartextcolor://white');
        }
      }).catch(() => {
        // Fallback: direct window setter
        try { window.despia = cmd; } catch(e) {}
        if (navigator.userAgent.toLowerCase().includes('android')) {
          try { window.despia = mode === 'light' ? 'statusbartextcolor://black' : 'statusbartextcolor://white'; } catch(e) {}
        }
      });
    }

    // Update apple-mobile-web-app-status-bar-style for light/dark
    const metaStatusStyle = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
    if (metaStatusStyle) {
      metaStatusStyle.setAttribute('content', mode === 'light' ? 'default' : 'black-translucent');
    }

    if (mode === 'light') {
      root.classList.remove('dark');
    } else {
      root.classList.add('dark');
    }
  };

  const applyFontSize = (sizeName) => {
    const size = fontSizes[sizeName] || fontSizes.normal;
    document.documentElement.style.fontSize = size;
    document.documentElement.style.setProperty('--app-font-size', size);
  };

  const applyFontFamily = (fontName) => {
    const legacyMap = { 'SF Pro': 'Serif', 'Helvetica': 'Merriweather' };
    const resolvedName = legacyMap[fontName] || fontName;
    const fontFamily = fontFamilies[resolvedName] || fontFamilies['Roboto'];
    document.documentElement.style.setProperty('--theme-font-family', fontFamily);
    document.body.style.fontFamily = fontFamily;
    if (resolvedName === 'Sans Serif') {
      document.documentElement.style.setProperty('--theme-heading-family', "'Playfair Display', serif");
    } else {
      document.documentElement.style.setProperty('--theme-heading-family', fontFamily);
    }
    return resolvedName;
  };

  const changeTheme = (themeName) => {
    currentThemeRef.current = themeName;
    setCurrentTheme(themeName);
    applyTheme(themeName, colorModeRef.current);
    localStorage.setItem('reveala-theme', themeName);
  };

  const changeFontSize = (sizeName) => {
    setCurrentFontSize(sizeName);
    applyFontSize(sizeName);
    localStorage.setItem('reveala-font-size', sizeName);
  };

  const changeFontFamily = (fontName) => {
    setCurrentFontFamily(fontName);
    applyFontFamily(fontName);
    localStorage.setItem('reveala-font-family', fontName);
  };

  const changeColorMode = (mode) => {
    colorModeRef.current = mode;
    setColorMode(mode);
    applyTheme(currentThemeRef.current, mode);
    localStorage.setItem('eili-color-mode', mode);
  };

  const toggleColorMode = () => {
    const newMode = colorModeRef.current === 'dark' ? 'light' : 'dark';
    changeColorMode(newMode);
  };

  return (
    <ThemeContext.Provider value={{
      currentTheme,
      changeTheme,
      themes,
      lightThemes,
      currentFontSize,
      changeFontSize,
      fontSizes,
      currentFontFamily,
      changeFontFamily,
      fontFamilies,
      colorMode,
      changeColorMode,
      toggleColorMode,
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
